<template>
  <div class="sms-policy-page o-container o-container--large">
    <div class="sms-policy-content o-section--bigger">
      <!-- English Content -->
      <div v-if="iso !== 'es'" class="policy-text">
        <h1>Vacancy Rewards – SMS Messaging Policy</h1>
        <p class="last-updated">Last Updated: February, 2026</p>

        <p>
          Vacancy Rewards (“we,” “our,” or “us”) respects your privacy and is
          committed to protecting your personal information. This SMS Messaging
          Policy explains how we collect, use, and manage mobile phone numbers
          when you opt in to receive SMS communications from us.
        </p>

        <h2>1. Program Description</h2>
        <p>
          By providing your mobile phone number and opting in, you agree to
          receive SMS messages from Vacancy Rewards related to:
        </p>
        <ul>
          <li>Membership information and benefits</li>
          <li>Reservation confirmations and travel updates</li>
          <li>Renewal reminders and account notifications</li>
          <li>Payment confirmations</li>
          <li>Customer service communications</li>
          <li>Promotional offers and travel opportunities</li>
        </ul>
        <p>
          Message frequency may vary depending on your interaction with our
          services and membership status.
        </p>

        <h2>2. Opt-In</h2>
        <p>You may opt in to receive SMS messages from Vacancy Rewards by:</p>
        <ul>
          <li>Checking the SMS consent box on our website forms</li>
          <li>Providing your phone number during membership enrollment</li>
          <li>Submitting your phone number through our member portal</li>
          <li>
            Providing consent during a recorded customer service interaction
          </li>
        </ul>
        <p>Consent to receive SMS messages is not a condition of purchase.</p>

        <h2>3. Opt-Out</h2>
        <p>
          You may opt out of receiving SMS messages at any time by replying:
        </p>
        <p class="command">STOP</p>
        <p>
          After you send STOP, you will receive a confirmation message and will
          no longer receive SMS communications from Vacancy Rewards unless you
          opt in again.
        </p>
        <p>For assistance, reply:</p>
        <p class="command">HELP</p>
        <p>Or contact us at:</p>
        <p>
          Email:
          <a href="mailto:support@vacancyrewards.com"
            >support@vacancyrewards.com</a
          >
        </p>
        <p>
          Website:
          <a href="https://vacancyrewards.com" target="_blank"
            >https://vacancyrewards.com</a
          >
        </p>

        <h2>4. Message & Data Rates</h2>
        <p>
          Message and data rates may apply depending on your mobile carrier and
          mobile plan.
        </p>
        <p>
          Supported carriers are not liable for delayed or undelivered messages.
        </p>

        <h2>5. Data Sharing & Privacy</h2>
        <p>
          Vacancy Rewards does not sell, rent, or share mobile phone numbers
          with third parties for marketing or promotional purposes.
        </p>
        <p>
          Mobile phone numbers collected for SMS communications are used solely
          for purposes directly related to Vacancy Rewards’ services.
        </p>
        <p>
          For more information, please review our
          <span
            @click.prevent="openPrivacyPolicy"
            style="color: #0472aa; cursor: pointer"
            >Privacy Policy</span
          >
        </p>

        <h2>6. Data Security</h2>
        <p>
          We implement reasonable administrative, technical, and physical
          safeguards designed to protect your personal information, including
          mobile phone numbers.
        </p>

        <h2>7. Changes to This Policy</h2>
        <p>
          Vacancy Rewards reserves the right to update or modify this SMS
          Messaging Policy at any time. Updates will be posted on this page with
          a revised effective date.
        </p>
      </div>

      <!-- Spanish Content -->
      <div v-else class="policy-text">
        <h1>Vacancy Rewards – Política de Mensajería SMS</h1>
        <p class="last-updated">Última actualización: Febrero, 2026</p>

        <p>
          Vacancy Rewards (“nosotros”, “nuestro”) respeta su privacidad y está
          comprometido con la protección de su información personal. Esta
          Política de Mensajería SMS explica cómo recopilamos, utilizamos y
          gestionamos los números de teléfono móvil cuando usted acepta recibir
          comunicaciones por SMS de nuestra parte.
        </p>

        <h2>1. Descripción del Programa</h2>
        <p>
          Al proporcionar su número de teléfono móvil y otorgar su
          consentimiento, usted acepta recibir mensajes SMS de Vacancy Rewards
          relacionados con:
        </p>
        <ul>
          <li>Información sobre su membresía y beneficios</li>
          <li>Confirmaciones de reservaciones y actualizaciones de viaje</li>
          <li>Recordatorios de renovación y notificaciones de cuenta</li>
          <li>Confirmaciones de pago</li>
          <li>Comunicaciones de atención al cliente</li>
          <li>Ofertas promocionales y oportunidades de viaje</li>
        </ul>
        <p>
          La frecuencia de los mensajes puede variar según su interacción con
          nuestros servicios y el estatus de su membresía.
        </p>

        <h2>2. Consentimiento (Opt-In)</h2>
        <p>
          Usted puede autorizar la recepción de mensajes SMS de Vacancy Rewards
          mediante:
        </p>
        <ul>
          <li>
            La selección de la casilla de consentimiento SMS en nuestros
            formularios web
          </li>
          <li>
            Proporcionando su número durante el proceso de inscripción a la
            membresía
          </li>
          <li>Registrando su número en el portal de socios</li>
          <li>
            Otorgando su consentimiento durante una interacción grabada con
            servicio al cliente
          </li>
        </ul>
        <p>
          El consentimiento para recibir mensajes SMS no es una condición para
          realizar una compra.
        </p>

        <h2>3. Cancelación de Suscripción (Opt-Out)</h2>
        <p>
          Puede cancelar la recepción de mensajes SMS en cualquier momento
          respondiendo:
        </p>
        <p class="command">STOP</p>
        <p>
          Después de enviar STOP, recibirá un mensaje de confirmación y dejará
          de recibir comunicaciones SMS de Vacancy Rewards, a menos que vuelva a
          otorgar su consentimiento.
        </p>
        <p>Para recibir ayuda, responda:</p>
        <p class="command">HELP</p>
        <p>O contáctenos en:</p>
        <p>
          Correo electrónico:
          <a href="mailto:support@vacancyrewards.com"
            >support@vacancyrewards.com</a
          >
        </p>
        <p>
          Sitio web:
          <a href="https://vacancyrewards.com" target="_blank"
            >https://vacancyrewards.com</a
          >
        </p>

        <h2>4. Tarifas de Mensajes y Datos</h2>
        <p>
          Pueden aplicarse tarifas por mensajes y datos según su operador móvil
          y plan contratado.
        </p>
        <p>
          Los operadores móviles no son responsables por mensajes retrasados o
          no entregados.
        </p>

        <h2>5. Privacidad y Uso de Datos</h2>
        <p>
          Vacancy Rewards no vende, renta ni comparte números de teléfono móvil
          con terceros para fines de marketing o promoción.
        </p>
        <p>
          Los números recopilados para comunicaciones SMS se utilizan
          exclusivamente para fines relacionados con los servicios de Vacancy
          Rewards.
        </p>
        <p>
          Para más información, consulte nuestra
          <span
            @click.prevent="openPrivacyPolicy"
            style="color: #0472aa; cursor: pointer"
            >Política de Privacidad</span
          >
        </p>

        <h2>6. Seguridad de la Información</h2>
        <p>
          Implementamos medidas administrativas, técnicas y físicas razonables
          para proteger su información personal, incluyendo su número de
          teléfono móvil.
        </p>

        <h2>7. Cambios a esta Política</h2>
        <p>
          Vacancy Rewards se reserva el derecho de actualizar o modificar esta
          Política de Mensajería SMS en cualquier momento. Las actualizaciones
          se publicarán en esta página con la fecha de revisión correspondiente.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      iso: "en",
    };
  },
  created() {
    this.iso = localStorage.getItem("iso") || "en";
    this.$bus.on("change-iso", () => {
      this.iso = localStorage.getItem("iso") || "en";
      this.$forceUpdate();
    });
  },
  methods: {
    openPrivacyPolicy() {
      // Trigger the privacy policy modal if possible, or link to it
      // Assuming Footer has a listener or a global event for this
      this.$bus.emit("cookies"); // Using existing event for terms/privacy if applicable, or logic from footer
    },
  },
};
</script>

<style scoped>
.sms-policy-page {
  padding-top: 2rem;
  padding-bottom: 4rem;
  background-color: #fff;
  min-height: 80vh;
}

.sms-policy-content {
  max-width: 800px;
  margin: 0 auto;
  font-family: "Open Sans", sans-serif; /* Assuming this font is used or similar */
  color: #333;
}

.policy-text h1 {
  font-size: 2rem;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.last-updated {
  font-style: italic;
  color: #666;
  margin-bottom: 2rem;
}

.policy-text h2 {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-top: 2rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
}

.policy-text p {
  line-height: 1.6;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.policy-text ul {
  list-style-type: disc;
  margin-left: 2rem;
  margin-bottom: 1rem;
}

.policy-text li {
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.command {
  font-weight: bold;
  font-family: monospace;
  background-color: #f4f4f4;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
}

a {
  color: #3498db;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}
</style>
